/**
 * SpecTab Component
 * Specification selection: manufacturer, membrane type, build-up, products
 */

import React, { useMemo } from 'react';
import { Card, Button, Select, Checkbox } from '../ui/FormElements';
import BuildUpVisual from './BuildUpVisual';
import { isPolytheneVcl } from '../../utils/calculations';
import {
  MEMBRANE_TYPES,
  BUILD_UP_TYPES,
  MEMBRANE_FIXING_METHODS,
  INSULATION_FIXING_METHODS,
  DEFAULT_SPEC
} from '../../constants/flatCalc';

const SpecTab = ({ data, onChange, manufacturers, products, loading, onNext }) => {
  const updateSpec = (field, value) => {
    const newSpec = { ...data, [field]: value };
    
    // Reset dependent fields when parent selection changes
    if (field === 'manufacturerId') {
      Object.keys(DEFAULT_SPEC).forEach(k => {
        if (k !== 'manufacturerId') newSpec[k] = DEFAULT_SPEC[k];
      });
      newSpec.manufacturerId = value ? parseInt(value) : null;
    }
    
    if (field === 'membraneType') {
      [
        'deckMembraneId', 'flashingMembraneId', 'detailMembraneId',
        'deckUnderlayId', 'deckCapId', 'flashingUnderlayId', 'flashingCapId',
        'detailUnderlayId', 'detailCapId', 'membraneFixingMethod',
        'deckAdhesiveId', 'detailAdhesiveId', 'useDetailAsDeckAdhesive'
      ].forEach(k => newSpec[k] = DEFAULT_SPEC[k]);
    }
    
    if (field === 'buildUp') {
      newSpec.vclId = null;
      newSpec.vclPrimerId = null;
      newSpec.useExistingAsVcl = false;
    }
    
    if (field === 'membraneFixingMethod' && value === 'mech_fix') {
      newSpec.deckAdhesiveId = null;
      newSpec.useDetailAsDeckAdhesive = false;
    }
    
    if (field === 'insulationFixingMethod') {
      if (value === 'mech_fix') {
        newSpec.insulationAdhesiveId = null;
        newSpec.insulationBondingArea = 0;
      } else {
        newSpec.fixingsPerBoard = 4;
      }
    }
    
    if (field === 'vclId') {
      newSpec.vclPrimerId = null;
    }
    
    onChange(newSpec);
  };

  // Filter products by manufacturer
  const filteredProducts = useMemo(() => {
    return data.manufacturerId
      ? products.filter(p => p.manufacturer_id === parseInt(data.manufacturerId))
      : [];
  }, [products, data.manufacturerId]);

  // Product category filters
  const singlePlyMembranes = filteredProducts.filter(p => 
    p.category === 'membrane' && 
    (p.sub_category?.includes('single_ply') || p.sub_category === 'single_ply_pvc')
  );
  const rbmUnderlays = filteredProducts.filter(p => 
    p.category === 'membrane' && p.sub_category?.includes('bituminous_under')
  );
  const rbmCapSheets = filteredProducts.filter(p => 
    p.category === 'membrane' && p.sub_category?.includes('bituminous_cap')
  );
  const vcls = filteredProducts.filter(p => p.category === 'vcl');
  const primers = filteredProducts.filter(p => p.category === 'primer');
  const adhesives = filteredProducts.filter(p => p.category === 'adhesive');
  const insulations = filteredProducts.filter(p => p.category === 'insulation');
  
  // Derived state
  const buildUpConfig = BUILD_UP_TYPES.find(b => b.id === data.buildUp);
  const isOverlay = data.buildUp === 'overlay';
  const isSinglePly = data.membraneType === 'single_ply';
  const isRbm = data.membraneType === 'rbm';
  const isAdheredMembrane = data.membraneFixingMethod === 'adhered';
  const isAdheredInsulation = data.insulationFixingMethod === 'adhered';
  const selectedVcl = products.find(p => p.id === parseInt(data.vclId));
  const vclNeedsPrimer = data.vclId && !isPolytheneVcl(selectedVcl);

  if (loading) {
    return <div className="text-center py-8 text-slate-400">Loading products...</div>;
  }

  return (
    <div className="space-y-6">
      {/* 1. System Setup */}
      <Card title="1. System Setup">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <Select
            label="Manufacturer"
            value={data.manufacturerId}
            onChange={(v) => updateSpec('manufacturerId', v)}
            options={manufacturers.map(m => ({ id: m.id, label: m.name }))}
            placeholder="-- Select Manufacturer --"
          />
          <Select
            label="Membrane Type"
            value={data.membraneType}
            onChange={(v) => updateSpec('membraneType', v)}
            options={MEMBRANE_TYPES}
            placeholder="-- Select Type --"
            disabled={!data.manufacturerId}
          />
          <Select
            label="Build-Up"
            value={data.buildUp}
            onChange={(v) => updateSpec('buildUp', v)}
            options={BUILD_UP_TYPES.map(b => ({ id: b.id, label: b.label }))}
            placeholder="-- Select Build-Up --"
            disabled={!data.membraneType}
          />
        </div>
      </Card>

      {/* 2. Single Ply Membrane Selection */}
      {isSinglePly && data.buildUp && (
        <Card title="2. Membrane Selection">
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Select
                label="Fixing Method"
                value={data.membraneFixingMethod}
                onChange={(v) => updateSpec('membraneFixingMethod', v)}
                options={MEMBRANE_FIXING_METHODS}
                placeholder="-- Select Method --"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <Select
                label="Deck Membrane"
                value={data.deckMembraneId}
                onChange={(v) => updateSpec('deckMembraneId', v ? parseInt(v) : null)}
                options={singlePlyMembranes.map(p => ({
                  id: p.id,
                  label: `${p.name} (${p.thickness_mm}mm)`
                }))}
                placeholder="-- Select --"
              />
              <Select
                label="Flashing Membrane"
                value={data.flashingMembraneId}
                onChange={(v) => updateSpec('flashingMembraneId', v ? parseInt(v) : null)}
                options={singlePlyMembranes.map(p => ({
                  id: p.id,
                  label: `${p.name} (${p.thickness_mm}mm)`
                }))}
                placeholder="-- Select --"
              />
              <Select
                label="Detail Membrane"
                value={data.detailMembraneId}
                onChange={(v) => updateSpec('detailMembraneId', v ? parseInt(v) : null)}
                options={singlePlyMembranes.map(p => ({
                  id: p.id,
                  label: `${p.name} (${p.thickness_mm}mm)`
                }))}
                placeholder="-- Select --"
              />
            </div>
          </div>
        </Card>
      )}

      {/* 2. RBM Membrane Selection */}
      {isRbm && data.buildUp && (
        <Card title="2. Membrane Selection (RBM)">
          <div className="space-y-6">
            {/* Deck Area */}
            <div>
              <h4 className="text-slate-300 font-medium mb-3">Deck Area</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Select
                  label="Underlay"
                  value={data.deckUnderlayId}
                  onChange={(v) => updateSpec('deckUnderlayId', v ? parseInt(v) : null)}
                  options={rbmUnderlays.map(p => ({ id: p.id, label: p.name }))}
                  placeholder="-- Select --"
                />
                <Select
                  label="Cap Sheet"
                  value={data.deckCapId}
                  onChange={(v) => updateSpec('deckCapId', v ? parseInt(v) : null)}
                  options={rbmCapSheets.map(p => ({ id: p.id, label: p.name }))}
                  placeholder="-- Select --"
                />
              </div>
            </div>
            
            {/* Flashing / Upstands */}
            <div>
              <h4 className="text-slate-300 font-medium mb-3">Flashing / Upstands</h4>
              <Checkbox
                label="Same as deck area"
                checked={data.flashingSameAsDeck}
                onChange={(v) => updateSpec('flashingSameAsDeck', v)}
              />
              {!data.flashingSameAsDeck && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-3">
                  <Select
                    label="Underlay"
                    value={data.flashingUnderlayId}
                    onChange={(v) => updateSpec('flashingUnderlayId', v ? parseInt(v) : null)}
                    options={rbmUnderlays.map(p => ({ id: p.id, label: p.name }))}
                    placeholder="-- Select --"
                  />
                  <Select
                    label="Cap Sheet"
                    value={data.flashingCapId}
                    onChange={(v) => updateSpec('flashingCapId', v ? parseInt(v) : null)}
                    options={rbmCapSheets.map(p => ({ id: p.id, label: p.name }))}
                    placeholder="-- Select --"
                  />
                </div>
              )}
            </div>
            
            {/* Details */}
            <div>
              <h4 className="text-slate-300 font-medium mb-3">Details</h4>
              <Checkbox
                label="Same as deck area"
                checked={data.detailSameAsDeck}
                onChange={(v) => updateSpec('detailSameAsDeck', v)}
              />
              {!data.detailSameAsDeck && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-3">
                  <Select
                    label="Underlay"
                    value={data.detailUnderlayId}
                    onChange={(v) => updateSpec('detailUnderlayId', v ? parseInt(v) : null)}
                    options={rbmUnderlays.map(p => ({ id: p.id, label: p.name }))}
                    placeholder="-- Select --"
                  />
                  <Select
                    label="Cap Sheet"
                    value={data.detailCapId}
                    onChange={(v) => updateSpec('detailCapId', v ? parseInt(v) : null)}
                    options={rbmCapSheets.map(p => ({ id: p.id, label: p.name }))}
                    placeholder="-- Select --"
                  />
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* 3. Adhesives (Single Ply) */}
      {isSinglePly && data.membraneFixingMethod && (
        <Card title="3. Adhesives">
          <div className="space-y-6">
            {isAdheredMembrane && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Select
                    label="Deck Adhesive"
                    value={data.useDetailAsDeckAdhesive ? '' : data.deckAdhesiveId}
                    onChange={(v) => updateSpec('deckAdhesiveId', v ? parseInt(v) : null)}
                    options={adhesives.map(p => ({ id: p.id, label: p.name }))}
                    placeholder="-- Select --"
                    disabled={data.useDetailAsDeckAdhesive}
                  />
                </div>
                <Checkbox
                  label="Use detail/contact adhesive as deck adhesive (calculated at 2x coverage)"
                  checked={data.useDetailAsDeckAdhesive}
                  onChange={(v) => updateSpec('useDetailAsDeckAdhesive', v)}
                />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Select
                label="Detail / Contact Adhesive"
                value={data.detailAdhesiveId}
                onChange={(v) => updateSpec('detailAdhesiveId', v ? parseInt(v) : null)}
                options={adhesives.map(p => ({ id: p.id, label: p.name }))}
                placeholder="-- Select --"
              />
            </div>
          </div>
        </Card>
      )}

      {/* 3. Deck Primer (RBM) */}
      {isRbm && data.buildUp && (
        <Card title="3. Deck Primer">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Select
              label="Deck Primer"
              value={data.deckPrimerId}
              onChange={(v) => updateSpec('deckPrimerId', v ? parseInt(v) : null)}
              options={primers.map(p => ({ id: p.id, label: p.name }))}
              placeholder="-- Select --"
            />
          </div>
          <p className="text-slate-500 text-sm mt-2">Required for RBM adhesion to deck</p>
        </Card>
      )}

      {/* 4. VCL & Primer */}
      {data.buildUp && (buildUpConfig?.requiresVcl || isOverlay) && (
        <Card title="4. VCL & Primer">
          {isOverlay && (
            <div className="mb-4">
              <Checkbox
                label="Use existing roofing system as VCL"
                checked={data.useExistingAsVcl}
                onChange={(v) => updateSpec('useExistingAsVcl', v)}
              />
            </div>
          )}
          {!data.useExistingAsVcl && (buildUpConfig?.requiresVcl || isOverlay) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Select
                label="VCL Product"
                value={data.vclId}
                onChange={(v) => updateSpec('vclId', v ? parseInt(v) : null)}
                options={vcls.map(p => ({ id: p.id, label: p.name }))}
                placeholder="-- Select --"
              />
              {vclNeedsPrimer && (
                <Select
                  label="VCL Primer"
                  value={data.vclPrimerId}
                  onChange={(v) => updateSpec('vclPrimerId', v ? parseInt(v) : null)}
                  options={primers.map(p => ({ id: p.id, label: p.name }))}
                  placeholder="-- Select --"
                />
              )}
            </div>
          )}
          {data.useExistingAsVcl && (
            <p className="text-slate-500 text-sm">
              Existing roofing system will act as vapour control layer.
            </p>
          )}
        </Card>
      )}

      {/* 5. Insulation */}
      {data.buildUp && (
        <Card title="5. Insulation">
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
              <Select
                label="Insulation Product"
                value={data.insulationId}
                onChange={(v) => updateSpec('insulationId', v ? parseInt(v) : null)}
                options={insulations.map(p => ({ id: p.id, label: p.name }))}
                placeholder="-- Select --"
              />
              <div>
                <label className="block text-slate-400 text-sm mb-2">Thickness (mm)</label>
                <input
                  type="number"
                  value={data.insulationThickness || ''}
                  onChange={(e) => updateSpec('insulationThickness', parseInt(e.target.value) || 0)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                  placeholder="e.g. 100"
                />
              </div>
              <Select
                label="Layers"
                value={data.insulationLayers || 1}
                onChange={(v) => updateSpec('insulationLayers', parseInt(v))}
                options={[
                  { id: 1, label: 'Single' },
                  { id: 2, label: 'Double' },
                  { id: 3, label: 'Triple' }
                ]}
              />
              <Select
                label="Fixing Method"
                value={data.insulationFixingMethod}
                onChange={(v) => updateSpec('insulationFixingMethod', v)}
                options={INSULATION_FIXING_METHODS}
                placeholder="-- Select --"
              />
            </div>
            
            {isAdheredInsulation && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-slate-700">
                <Select
                  label="Insulation Adhesive"
                  value={data.insulationAdhesiveId}
                  onChange={(v) => updateSpec('insulationAdhesiveId', v ? parseInt(v) : null)}
                  options={adhesives.map(p => ({ id: p.id, label: p.name }))}
                  placeholder="-- Select --"
                />
              </div>
            )}
            
            {data.insulationFixingMethod === 'mech_fix' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-slate-700">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Fixings per Board</label>
                  <input
                    type="number"
                    value={data.fixingsPerBoard || 4}
                    onChange={(e) => updateSpec('fixingsPerBoard', parseInt(e.target.value) || 4)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
                    min="1"
                    max="12"
                  />
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Build-Up Visual */}
      <BuildUpVisual spec={data} products={products} />
      
      {/* Navigation */}
      <div className="flex justify-end mt-6">
        <Button onClick={onNext}>Next: Areas →</Button>
      </div>
    </div>
  );
};

export default SpecTab;
